import React, { useEffect, useState } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
import { useAppSelector } from '../store';
import { usePermissions, Permission } from '../hooks/usePermissions';
import { getCurrentMenuTree, type Menu as BackendMenu } from '../services/menuService';
import Layout from '../components/Layout';
import Login from '../pages/Login';
import Dashboard from '../pages/Dashboard';
import ProviderDashboard from '../pages/ProviderDashboard';
import ProviderIncomeFlows from '../pages/ProviderIncomeFlows';
import ProviderSettlementFlows from '../pages/ProviderSettlementFlows';
import UserManagement from '../pages/UserManagement';
import MemberManagement from '../pages/MemberManagement';
import SystemSettings from '../pages/SystemSettings';
import MeituanCancelLog from '../pages/MeituanCancelLog';
import MeituanConfig from '../pages/MeituanConfig';
import MeituanInventoryConfig from '../pages/MeituanInventoryConfig';
import MeituanFinancialHealth from '../pages/MeituanFinancialHealth';
import MeituanOrderConfirmLog from '../pages/MeituanOrderConfirmLog';
import MeituanOrderFulfillLog from '../pages/MeituanOrderFulfillLog';
import MeituanReservationChangeLog from '../pages/MeituanReservationChangeLog';
import MeituanReservationOrderLog from '../pages/MeituanReservationOrderLog';
import MeituanShopManagement from '../pages/MeituanShopManagement';
import MiniProgramUserConfig from '../pages/MiniProgramUserConfig';
import MiniProgramStaffConfig from '../pages/MiniProgramStaffConfig';
import MyInfo from '../pages/MyInfo';
import Profile from '../pages/Profile';
import PersonalSettings from '../pages/PersonalSettings';
import Notifications from '../pages/Notifications';
import RoleManagement from '../pages/RoleManagement';
import MenuManagement from '../pages/MenuManagement';
import RedisMonitor from '../pages/RedisMonitor';
import DictManagement from '../pages/DictManagement';
import RegionManagement from '../pages/RegionManagement';
import LoginLog from '../pages/LoginLog';
import MeituanVerifyLog from '../pages/MeituanVerifyLog';
import OperationLog from '../pages/OperationLog';
import FileConfig from '../pages/FileConfig';
import FileManagement from '../pages/FileManagement';
import SmsChannel from '../pages/sms/SmsChannel';
import SmsTemplate from '../pages/sms/SmsTemplate';
import SmsLog from '../pages/sms/SmsLog';
import { WorkflowTodo, WorkflowDone, WorkflowInitiated, WorkflowCC, WorkflowDefinitions, WorkflowDelegates, WorkflowStart, FormDesigner } from '../pages/workflow';
import { NotificationConfig, NotificationLog } from '../pages/scheduled';
import { PaymentApp, PaymentOrder, OrderPool, RefundOrder, TransferOrder, PaymentNotify } from '../pages/payment';
import { ServiceCategory, ServiceProduct, ServiceCoupon, ServiceTimeConfig } from '../pages/service';
import { MemberRecharge, MemberWallet } from '../pages/member';
import { FinanceReport } from '../pages/finance';
import {
  AutoDispatchConfig,
  CertificationReview,
  StaffManagement,
  StaffServiceProcessConfig,
  StaffDepositPayment,
  StaffDepositRefundRequest,
  StaffTraining,
  StaffWithdrawReview,
  StaffWithdrawRule,
} from '../pages/staff';
import NotFound from '../pages/NotFound';
import NoPermission from '../components/NoPermission';

const alwaysAllowedPaths = new Set(['/dashboard', '/provider-dashboard', '/profile', '/settings', '/notifications']);

const flattenMenuPaths = (menus: BackendMenu[]): string[] => {
  const paths: string[] = [];

  const walk = (items: BackendMenu[]) => {
    items.forEach((item) => {
      if (item.path?.startsWith('/')) {
        paths.push(item.path);
      }
      if (item.children?.length) {
        walk(item.children);
      }
    });
  };

  walk(menus);
  return paths;
};

const isPathAllowedByMenu = (pathname: string, menuPaths: string[]) => {
  if (pathname === '/' || alwaysAllowedPaths.has(pathname)) return true;

  return menuPaths.some((menuPath) => {
    if (pathname === menuPath) return true;
    if (pathname.startsWith(`${menuPath}/`)) return true;
    if (pathname.startsWith('/finance/report/') && menuPath.startsWith('/finance/report/')) return true;
    return false;
  });
};

// 受保护的路由组件
const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  requiredPermission?: Permission;
}> = ({ children, requiredPermission }) => {
  // 从Redux store中获取用户信息
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const [menuPaths, setMenuPaths] = useState<string[]>([]);
  const [menuLoaded, setMenuLoaded] = useState(false);
  const [menuLoadFailed, setMenuLoadFailed] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !user || !requiredPermission) {
      setMenuLoaded(true);
      return;
    }

    let ignore = false;

    const loadMenus = async () => {
      setMenuLoaded(false);
      setMenuLoadFailed(false);

      try {
        const response = await getCurrentMenuTree();
        if (!ignore) {
          setMenuPaths(flattenMenuPaths(response.data || []));
          setMenuLoaded(true);
        }
      } catch {
        if (!ignore) {
          setMenuPaths([]);
          setMenuLoadFailed(true);
          setMenuLoaded(true);
        }
      }
    };

    void loadMenus();

    return () => {
      ignore = true;
    };
  }, [isAuthenticated, user, requiredPermission]);

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // 权限检查
  if (requiredPermission && !menuLoaded) {
    return null;
  }

  const allowedByMenu = requiredPermission ? isPathAllowedByMenu(location.pathname, menuPaths) : true;
  const allowedByLegacyPermission = menuLoadFailed && requiredPermission ? hasPermission(requiredPermission) : false;

  if (requiredPermission && !allowedByMenu && !allowedByLegacyPermission) {
    return <NoPermission
      title="权限不足"
      description={`您没有访问此页面的权限，当前角色：${user.role}`}
    />;
  }

  return <>{children}</>;
};

// 路由配置
const routesConfig = [
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <Layout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '',
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: 'dashboard',
        element: <Dashboard />,
      },
      {
        path: 'provider-dashboard',
        element: (
          <ProtectedRoute>
            <ProviderDashboard />
          </ProtectedRoute>
        ),
      },
      {
        path: 'user-management',
        element: (
          <ProtectedRoute requiredPermission={Permission.USER_MANAGEMENT}>
            <UserManagement />
          </ProtectedRoute>
        ),
      },
      {
        path: 'system/user',
        element: (
          <ProtectedRoute>
            <UserManagement />
          </ProtectedRoute>
        ),
      },
      {
        path: 'role-management',
        element: (
          <ProtectedRoute requiredPermission={Permission.ROLE_MANAGEMENT}>
            <RoleManagement />
          </ProtectedRoute>
        ),
      },
      {
        path: 'system/role',
        element: (
          <ProtectedRoute>
            <RoleManagement />
          </ProtectedRoute>
        ),
      },
      {
        path: 'menu-management',
        element: (
          <ProtectedRoute>
            <MenuManagement />
          </ProtectedRoute>
        ),
      },
      {
        path: 'system/menu',
        element: (
          <ProtectedRoute>
            <MenuManagement />
          </ProtectedRoute>
        ),
      },
      {
        path: 'system/service-provider-management',
        element: (
          <ProtectedRoute>
            <UserManagement />
          </ProtectedRoute>
        ),
      },
      {
        path: 'system/my-info',
        element: (
          <ProtectedRoute>
            <MyInfo />
          </ProtectedRoute>
        ),
      },
      {
        path: 'system/provider-settlement-flows',
        element: (
          <ProtectedRoute>
            <ProviderSettlementFlows />
          </ProtectedRoute>
        ),
      },
      {
        path: 'system/provider-income-flows',
        element: (
          <ProtectedRoute>
            <ProviderIncomeFlows />
          </ProtectedRoute>
        ),
      },
      {
        path: 'redis-monitor',
        element: (
          <ProtectedRoute>
            <RedisMonitor />
          </ProtectedRoute>
        ),
      },
      {
        path: 'system/redis',
        element: (
          <ProtectedRoute>
            <RedisMonitor />
          </ProtectedRoute>
        ),
      },
      {
        path: 'dict-management',
        element: (
          <ProtectedRoute>
            <DictManagement />
          </ProtectedRoute>
        ),
      },
      {
        path: 'system/dict',
        element: (
          <ProtectedRoute>
            <DictManagement />
          </ProtectedRoute>
        ),
      },
      {
        path: 'region-management',
        element: (
          <ProtectedRoute>
            <RegionManagement />
          </ProtectedRoute>
        ),
      },
      {
        path: 'system/region',
        element: (
          <ProtectedRoute>
            <RegionManagement />
          </ProtectedRoute>
        ),
      },
      {
        path: 'logs/login',
        element: (
          <ProtectedRoute>
            <LoginLog />
          </ProtectedRoute>
        ),
      },
      {
        path: 'logs/operation',
        element: (
          <ProtectedRoute>
            <OperationLog />
          </ProtectedRoute>
        ),
      },
      {
        path: 'logs/meituan-verify',
        element: (
          <ProtectedRoute>
            <MeituanVerifyLog />
          </ProtectedRoute>
        ),
      },
      {
        path: 'files/config',
        element: (
          <ProtectedRoute>
            <FileConfig />
          </ProtectedRoute>
        ),
      },
      {
        path: 'files/management',
        element: (
          <ProtectedRoute>
            <FileManagement />
          </ProtectedRoute>
        ),
      },
      {
        path: 'sms/channel',
        element: (
          <ProtectedRoute>
            <SmsChannel />
          </ProtectedRoute>
        ),
      },
      {
        path: 'sms/template',
        element: (
          <ProtectedRoute>
            <SmsTemplate />
          </ProtectedRoute>
        ),
      },
      {
        path: 'sms/log',
        element: (
          <ProtectedRoute>
            <SmsLog />
          </ProtectedRoute>
        ),
      },
      {
        path: 'scheduled/notification-config',
        element: (
          <ProtectedRoute>
            <NotificationConfig />
          </ProtectedRoute>
        ),
      },
      {
        path: 'scheduled/notification-log',
        element: (
          <ProtectedRoute>
            <NotificationLog />
          </ProtectedRoute>
        ),
      },
      {
        path: 'payment/app',
        element: (
          <ProtectedRoute>
            <PaymentApp />
          </ProtectedRoute>
        ),
      },
      {
        path: 'payment/order',
        element: (
          <ProtectedRoute>
            <PaymentOrder />
          </ProtectedRoute>
        ),
      },
      {
        path: 'payment/order-pool',
        element: (
          <ProtectedRoute>
            <OrderPool />
          </ProtectedRoute>
        ),
      },
      {
        path: 'payment/refund',
        element: (
          <ProtectedRoute>
            <RefundOrder />
          </ProtectedRoute>
        ),
      },
      {
        path: 'payment/transfer',
        element: (
          <ProtectedRoute>
            <TransferOrder />
          </ProtectedRoute>
        ),
      },
      {
        path: 'payment/notify',
        element: (
          <ProtectedRoute>
            <PaymentNotify />
          </ProtectedRoute>
        ),
      },
      {
        path: 'finance/report',
        element: <Navigate to="/finance/report/overview" replace />,
      },
      {
        path: 'finance/report/overview',
        element: (
          <ProtectedRoute>
            <FinanceReport tab="overview" />
          </ProtectedRoute>
        ),
      },
      {
        path: 'finance/report/order',
        element: (
          <ProtectedRoute>
            <FinanceReport tab="order" />
          </ProtectedRoute>
        ),
      },
      {
        path: 'finance/report/aftersale',
        element: (
          <ProtectedRoute>
            <FinanceReport tab="aftersale" />
          </ProtectedRoute>
        ),
      },
      {
        path: 'finance/report/withdraw',
        element: (
          <ProtectedRoute>
            <FinanceReport tab="withdraw" />
          </ProtectedRoute>
        ),
      },
      {
        path: 'finance/report/frozen',
        element: (
          <ProtectedRoute>
            <FinanceReport tab="frozen" />
          </ProtectedRoute>
        ),
      },
      {
        path: 'finance/report/service',
        element: (
          <ProtectedRoute>
            <FinanceReport tab="service" />
          </ProtectedRoute>
        ),
      },
      {
        path: 'finance/report/deposit',
        element: (
          <ProtectedRoute>
            <FinanceReport tab="deposit" />
          </ProtectedRoute>
        ),
      },
      {
        path: 'finance/report/provider-settlement',
        element: (
          <ProtectedRoute>
            <FinanceReport tab="providerSettlement" />
          </ProtectedRoute>
        ),
      },
      {
        path: 'finance/report/provider-income',
        element: (
          <ProtectedRoute>
            <FinanceReport tab="providerIncome" />
          </ProtectedRoute>
        ),
      },
      {
        path: 'service/category',
        element: (
          <ProtectedRoute>
            <ServiceCategory />
          </ProtectedRoute>
        ),
      },
      {
        path: 'service/product',
        element: (
          <ProtectedRoute>
            <ServiceProduct />
          </ProtectedRoute>
        ),
      },
      {
        path: 'service/coupon',
        element: (
          <ProtectedRoute>
            <ServiceCoupon />
          </ProtectedRoute>
        ),
      },
      {
        path: 'service/time',
        element: (
          <ProtectedRoute>
            <ServiceTimeConfig />
          </ProtectedRoute>
        ),
      },
      {
        path: 'staff/certification-review',
        element: (
          <ProtectedRoute>
            <CertificationReview />
          </ProtectedRoute>
        ),
      },
      {
        path: 'staff/management',
        element: (
          <ProtectedRoute>
            <StaffManagement />
          </ProtectedRoute>
        ),
      },
      {
        path: 'staff/service-process',
        element: (
          <ProtectedRoute>
            <StaffServiceProcessConfig />
          </ProtectedRoute>
        ),
      },
      {
        path: 'staff/training',
        element: (
          <ProtectedRoute>
            <StaffTraining />
          </ProtectedRoute>
        ),
      },
      {
        path: 'staff/auto-dispatch-config',
        element: (
          <ProtectedRoute>
            <AutoDispatchConfig />
          </ProtectedRoute>
        ),
      },
      {
        path: 'staff/withdraw-review',
        element: (
          <ProtectedRoute>
            <StaffWithdrawReview />
          </ProtectedRoute>
        ),
      },
      {
        path: 'staff/deposit-payment',
        element: (
          <ProtectedRoute>
            <StaffDepositPayment />
          </ProtectedRoute>
        ),
      },
      {
        path: 'staff/deposit-refund-request',
        element: (
          <ProtectedRoute>
            <StaffDepositRefundRequest />
          </ProtectedRoute>
        ),
      },
      {
        path: 'staff/withdraw-rule',
        element: (
          <ProtectedRoute>
            <StaffWithdrawRule />
          </ProtectedRoute>
        ),
      },
      {
        path: 'member-management',
        element: (
          <ProtectedRoute requiredPermission={Permission.MEMBER_MANAGEMENT}>
            <MemberManagement />
          </ProtectedRoute>
        ),
      },
      {
        path: 'member/list',
        element: (
          <ProtectedRoute requiredPermission={Permission.MEMBER_MANAGEMENT}>
            <MemberManagement />
          </ProtectedRoute>
        ),
      },
      {
        path: 'member/recharge',
        element: (
          <ProtectedRoute>
            <MemberRecharge />
          </ProtectedRoute>
        ),
      },
      {
        path: 'member/wallet',
        element: (
          <ProtectedRoute>
            <MemberWallet />
          </ProtectedRoute>
        ),
      },
      {
        path: 'mini-program/user-config',
        element: (
          <ProtectedRoute requiredPermission={Permission.SYSTEM_SETTINGS}>
            <MiniProgramUserConfig />
          </ProtectedRoute>
        ),
      },
      {
        path: 'mini-program/staff-config',
        element: (
          <ProtectedRoute requiredPermission={Permission.SYSTEM_SETTINGS}>
            <MiniProgramStaffConfig />
          </ProtectedRoute>
        ),
      },
      {
        path: 'system-settings',
        element: (
          <ProtectedRoute requiredPermission={Permission.SYSTEM_SETTINGS}>
            <SystemSettings />
          </ProtectedRoute>
        ),
      },
      {
        path: 'system/settings',
        element: (
          <ProtectedRoute>
            <SystemSettings />
          </ProtectedRoute>
        ),
      },
      {
        path: 'system/meituan-inventory-config',
        element: (
          <ProtectedRoute requiredPermission={Permission.SYSTEM_SETTINGS}>
            <MeituanInventoryConfig />
          </ProtectedRoute>
        ),
      },
      {
        path: 'system/meituan-financial-health',
        element: (
          <ProtectedRoute requiredPermission={Permission.SYSTEM_SETTINGS}>
            <MeituanFinancialHealth />
          </ProtectedRoute>
        ),
      },
      {
        path: 'meituan/config',
        element: (
          <ProtectedRoute requiredPermission={Permission.SYSTEM_SETTINGS}>
            <MeituanConfig />
          </ProtectedRoute>
        ),
      },
      {
        path: 'meituan/shops',
        element: (
          <ProtectedRoute requiredPermission={Permission.SYSTEM_SETTINGS}>
            <MeituanShopManagement />
          </ProtectedRoute>
        ),
      },
      {
        path: 'meituan/order-confirm-logs',
        element: (
          <ProtectedRoute requiredPermission={Permission.SYSTEM_SETTINGS}>
            <MeituanOrderConfirmLog />
          </ProtectedRoute>
        ),
      },
      {
        path: 'meituan/order-fulfill-logs',
        element: (
          <ProtectedRoute requiredPermission={Permission.SYSTEM_SETTINGS}>
            <MeituanOrderFulfillLog />
          </ProtectedRoute>
        ),
      },
      {
        path: 'meituan/reservation-change-logs',
        element: (
          <ProtectedRoute requiredPermission={Permission.SYSTEM_SETTINGS}>
            <MeituanReservationChangeLog />
          </ProtectedRoute>
        ),
      },
      {
        path: 'meituan/reservation-order-logs',
        element: (
          <ProtectedRoute requiredPermission={Permission.SYSTEM_SETTINGS}>
            <MeituanReservationOrderLog />
          </ProtectedRoute>
        ),
      },
      {
        path: 'meituan/cancel-logs',
        element: (
          <ProtectedRoute requiredPermission={Permission.SYSTEM_SETTINGS}>
            <MeituanCancelLog />
          </ProtectedRoute>
        ),
      },
      {
        path: 'profile',
        element: (
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings',
        element: (
          <ProtectedRoute>
            <PersonalSettings />
          </ProtectedRoute>
        ),
      },
      {
        path: 'notifications',
        element: (
          <ProtectedRoute>
            <Notifications />
          </ProtectedRoute>
        ),
      },
      {
        path: 'workflow/todo',
        element: (
          <ProtectedRoute>
            <WorkflowTodo />
          </ProtectedRoute>
        ),
      },
      {
        path: 'workflow/done',
        element: (
          <ProtectedRoute>
            <WorkflowDone />
          </ProtectedRoute>
        ),
      },
      {
        path: 'workflow/initiated',
        element: (
          <ProtectedRoute>
            <WorkflowInitiated />
          </ProtectedRoute>
        ),
      },
      {
        path: 'workflow/cc',
        element: (
          <ProtectedRoute>
            <WorkflowCC />
          </ProtectedRoute>
        ),
      },
      {
        path: 'workflow/start',
        element: (
          <ProtectedRoute>
            <WorkflowStart />
          </ProtectedRoute>
        ),
      },
      {
        path: 'workflow/definitions',
        element: (
          <ProtectedRoute>
            <WorkflowDefinitions />
          </ProtectedRoute>
        ),
      },
      {
        path: 'workflow/delegates',
        element: (
          <ProtectedRoute>
            <WorkflowDelegates />
          </ProtectedRoute>
        ),
      },
      {
        path: 'workflow/form-designer',
        element: (
          <ProtectedRoute>
            <FormDesigner />
          </ProtectedRoute>
        ),
      },
    ],
  },
  {
    path: '*',
    element: <NotFound />,
  },
];

// 路由组件
const AppRouter: React.FC = () => {
  return useRoutes(routesConfig);
};

export default AppRouter;
